import { useEffect, useRef, useState } from "react";
import { canAnimate } from "../../lib/motion";

const COUNT_UP_DURATION_MS = 700;

/**
 * Eases a whole number towards `target` (easeOutCubic) — from zero on mount, and from
 * whatever is on screen when the target changes, so a scope switch reads as the figure
 * moving rather than swapping.
 */
export function useCountUp(
  target: number,
  durationMs = COUNT_UP_DURATION_MS,
): number {
  const [display, setDisplay] = useState(() => (canAnimate() ? 0 : target));
  const displayRef = useRef(display);

  useEffect(() => {
    if (!canAnimate()) {
      displayRef.current = target;
      setDisplay(target);
      return;
    }

    const from = displayRef.current;
    if (from === target) return;

    let raf = 0;
    // Anchor on the first frame: rAF timestamps and `performance.now()` can carry
    // different time origins, which would send the first progress reading off-scale.
    let start: number | null = null;
    const tick = (now: number) => {
      start ??= now;
      const progress = Math.min((now - start) / durationMs, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      displayRef.current = Math.round(from + (target - from) * eased);
      setDisplay(displayRef.current);
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);

  return display;
}

/** Renders a count-up figure as bare text so callers keep their own typography. */
export function AnimatedNumber({
  value,
  durationMs,
}: {
  value: number;
  durationMs?: number;
}) {
  return <>{useCountUp(value, durationMs)}</>;
}
